import { AlertTriangle, Bell, CheckCheck, Info, Receipt, RefreshCw } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/shared/lib/cn';

type NotifType = 'stock' | 'sale' | 'sync' | 'info';

interface Notif {
  id: string;
  type: NotifType;
  title: string;
  desc: string;
  time: string;
  read: boolean;
  to?: string;
}

const ICONS: Record<NotifType, { Icon: typeof Bell; className: string }> = {
  stock: { Icon: AlertTriangle, className: 'bg-warning/15 text-warning' },
  sale: { Icon: Receipt, className: 'bg-success/15 text-success' },
  sync: { Icon: RefreshCw, className: 'bg-primary/15 text-primary' },
  info: { Icon: Info, className: 'bg-muted text-muted-foreground' },
};

export function NotificationsMenu() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Seed with representative items (no dedicated notifications API yet).
  const [items, setItems] = useState<Notif[]>(() => [
    {
      id: '1',
      type: 'stock',
      title: t('notifications.low_stock_title', 'تنبيه مخزون منخفض'),
      desc: t('notifications.low_stock_desc', '5 منتجات وصلت حدّ التنبيه'),
      time: t('notifications.min_ago', 'منذ {{n}} دقيقة', { n: 10 }),
      read: false,
      to: '/app/dashboard',
    },
    {
      id: '2',
      type: 'sale',
      title: t('notifications.new_sale_title', 'عملية بيع جديدة'),
      desc: t('notifications.new_sale_desc', 'تم تسجيل فاتورة بقيمة 1,196.32 ج.م'),
      time: t('notifications.min_ago', 'منذ {{n}} دقيقة', { n: 25 }),
      read: false,
      to: '/app/sales',
    },
    {
      id: '3',
      type: 'sync',
      title: t('notifications.synced_title', 'تمت المزامنة'),
      desc: t('notifications.synced_desc', 'تمت مزامنة مبيعات وضع عدم الاتصال'),
      time: t('notifications.hour_ago', 'منذ ساعة'),
      read: true,
    },
  ]);

  const unread = useMemo(() => items.filter((n) => !n.read).length, [items]);
  const markAllRead = () => setItems((rows) => rows.map((n) => ({ ...n, read: true })));
  const open = (n: Notif) => {
    setItems((rows) => rows.map((r) => (r.id === n.id ? { ...r, read: true } : r)));
    if (n.to) navigate(n.to);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label={t('notifications.title', 'Notifications')}
        >
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span className="absolute -end-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold leading-none text-destructive-foreground">
              {unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-80 rounded-xl p-0">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-3 py-2.5">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold">{t('notifications.title', 'الإشعارات')}</span>
            {unread > 0 && (
              <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold text-primary">
                {unread}
              </span>
            )}
          </div>
          {unread > 0 && (
            <button
              type="button"
              onClick={markAllRead}
              className="flex items-center gap-1 text-[11px] font-medium text-muted-foreground transition hover:text-primary"
            >
              <CheckCheck className="h-3.5 w-3.5" />
              {t('notifications.mark_all_read', 'تعيين الكل كمقروء')}
            </button>
          )}
        </div>

        {/* List */}
        {items.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
            <Bell className="h-8 w-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">
              {t('notifications.empty', 'لا توجد إشعارات')}
            </p>
          </div>
        ) : (
          <ScrollArea className="max-h-80">
            <ul className="flex flex-col py-1">
              {items.map((n) => {
                const { Icon, className } = ICONS[n.type];
                return (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => open(n)}
                      className={cn(
                        'flex w-full items-start gap-3 px-3 py-2.5 text-start transition hover:bg-muted/60',
                        !n.read && 'bg-primary/[0.04]',
                      )}
                    >
                      <span
                        className={cn(
                          'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                          className,
                        )}
                      >
                        <Icon className="h-4 w-4" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2">
                          <span className="truncate text-sm font-medium">{n.title}</span>
                          {!n.read && <span className="h-2 w-2 shrink-0 rounded-full bg-primary" />}
                        </span>
                        <span className="line-clamp-2 text-xs text-muted-foreground">{n.desc}</span>
                        <span className="mt-0.5 block text-[10px] text-muted-foreground/70">
                          {n.time}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </ScrollArea>
        )}

        {/* Footer */}
        <div className="border-t p-1.5">
          <button
            type="button"
            onClick={() => navigate('/app/settings/notifications')}
            className="w-full rounded-lg py-2 text-center text-xs font-medium text-primary transition hover:bg-primary/5"
          >
            {t('notifications.view_all', 'عرض كل الإشعارات')}
          </button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
